
// src/pages/Login.js
import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig'; // runs the 401/403 logout logic globally
import { useNavigate, Link } from 'react-router-dom';
import '../Style/Login.css';
import '../Style/Responsive.css';



// Simple, battle-tested email regex (RFC-5322-ish)
const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData]         = useState({ email: '', password: '' });
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [loading, setLoading]           = useState(false);   // ← real loading state
  const [acceptedTerms, setAcceptedTerms] = useState(false);
// Cooldown state for too many attempts
const [cooldown, setCooldown] = useState(false);

  // Clear “incorrect password” error once emptied
  useEffect(() => {
    if (
      error.toLowerCase().includes('password') &&
      formData.password === ''
    ) {
      setError('');
    }
  }, [formData.password, error]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setSuccess('');
    // Live email validation
    if (name === 'email') {
      if (value && !emailPattern.test(value)) {
        setError('Please enter a valid email address.');
      } else if (error.toLowerCase().includes('valid email')) {
        setError('');
      }
    } else {
      // clear other errors on typing
      setError('');
    }
  };

  const handleTermsChange = e => {
    setAcceptedTerms(e.target.checked);
    if (error.toLowerCase().includes('terms')) setError('');
  };

const handleLogin = async (e) => {
  e.preventDefault();
  setError('');
  setSuccess('');

  // Front-end gates
  if (!emailPattern.test(formData.email)) {
    setError('Please enter a valid email address.');
    return;
  }
  if (!acceptedTerms) {
    setError('Accept the terms and conditions.');
    return;
  }

  setLoading(true);
  const email = formData.email.trim().toLowerCase();

  try {
    const resp = await axios.post(
      `${process.env.REACT_APP_API_BASE_URL}/login/`,
      {
        email,
        password: formData.password,
        frontend_url: window.location.origin,
      }
    );

    // Persist & redirect
    localStorage.setItem('token', resp.data.token);
    localStorage.setItem('user_type', resp.data.user_type);

    // 30-minute auto-logout
    setTimeout(() => {
      localStorage.removeItem('token');
      localStorage.removeItem('user_type');
      axios.defaults.headers.common['Authorization'] = '';
      navigate('/login');
    }, 30 * 60 * 1000);

    // final redirect
    if (resp.data.user_type === 'investee') navigate('/investee-page');
    else if (resp.data.user_type === 'investor') navigate('/investor-page');
    else navigate('/');

  } catch (err) {
    console.error('Login error:', err);
    const resp = err.response;

    if (resp) {
      const { status, data } = resp;

      if (status === 429) {
        setCooldown(true);
        setTimeout(() => setCooldown(false), 5 * 60 * 1000);
        setError('Too many login attempts. Please try again after 5 minutes.');
        return;
      }

      if (status === 428 && data.detail === 'PASSWORD_EXPIRED') {
        setError('Invalid username or password.');
        return;
      }

      setError('Invalid username or password.');
    } else {
      setError('Network error. Please check your connection.');
    }
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="login-container">
      <div className="login-box">
        <h2>Login to Your Account</h2>

        {error   && <p className="error-message">{error}</p>}
        {success && <p className="success-message">{success}</p>}

        <form onSubmit={handleLogin} className="login-form" noValidate>
          <div id="form-group">
            <label>Email:</label>
            <input
              id="email-input"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}                      // ← disabled when loading
              autoComplete="off"   
            />
          </div>

          <div id="form-group">
            <label>Password:</label>
            <input
              id="password-input"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              autoComplete="new-password" 
            />
          </div>

          <div className="form-group terms-group" id="terms-group">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={handleTermsChange}
              disabled={loading}
            />
            <label htmlFor="terms" id="terms-label">
              I accept the{' '}
              <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                terms and conditions
              </a>.
            </label>
          </div>

          <button
            id="log-btn"
            type="submit"
            className="login-button"
            disabled={loading || cooldown}                    // ← disabled when loading
          >
           {loading ? 'Logging in…' : cooldown ? 'Please wait…' : 'Log In'}   
          </button>
        </form>

        <div className="reset-password-link">
          <Link to="/reset-password">Forgot Password?</Link>
        </div>

        <div className="register-link">
          <p>
            Don't have an account yet? <Link to="/register">Register</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
